import type { ReactNode } from 'react';
import { IconExclamationMark, IconLock, IconPackage } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import s from './kit.module.css';

/** Trạng thái màn như prop `state` của board Mint. */
export type ScreenState = 'ready' | 'loading' | 'empty' | 'error' | 'noperm';

/** Khung trạng thái chung: loading = skeleton (không spinner cả trang) · empty · error (có Thử lại) · noperm.
 *  `ready` thì trả về children. */
export function StateView({
  state,
  title,
  desc,
  icon,
  action,
  skeleton,
  children,
}: {
  state: ScreenState;
  title?: string;
  desc?: ReactNode;
  icon?: ReactNode;
  /** nút: empty → tạo mới (chủ chốt) · error → Thử lại (viền) */
  action?: ReactNode;
  /** khung skeleton riêng giữ đúng hình nội dung thật; mặc định 7 dòng */
  skeleton?: ReactNode;
  children?: ReactNode;
}) {
  const tr = useT();
  if (state === 'ready') return <>{children}</>;
  if (state === 'loading') return <div aria-busy="true" aria-label={tr('common.loading')}>{skeleton ?? <Skeleton />}</div>;
  const kind = state === 'error' ? s.err : state === 'noperm' ? s.lock : '';
  const ic =
    icon ??
    (state === 'error' ? <IconExclamationMark size={26} stroke={2.4} /> : state === 'noperm' ? <IconLock size={26} stroke={1.8} /> : <IconPackage size={26} stroke={1.8} />);
  const t = title ?? (state === 'error' ? tr('state.error') : state === 'noperm' ? tr('state.nopermTitle') : tr('state.emptyTitle'));
  return (
    <div className={s.state} role={state === 'error' ? 'alert' : undefined}>
      <div className={`${s.stateIc} ${kind}`} aria-hidden="true">
        {ic}
      </div>
      <h2 className={s.stateTitle}>{t}</h2>
      {desc && <p className={s.stateDesc}>{desc}</p>}
      {action && <div className={s.stateAct}>{action}</div>}
    </div>
  );
}

export function Skeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div className={s.skel} aria-hidden="true">
      <div className={`${s.sk} ${s.skStrong}`} style={{ width: '40%', height: 14 }} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={s.skRow}>
          <div className={s.sk} style={{ width: '28%' }} />
          <div className={s.sk} style={{ flexGrow: 1 }} />
        </div>
      ))}
    </div>
  );
}
